import Seo from "@/components/Seo";
import { useLang, route } from "../i18n";
import { useCompany } from "../components/SiteContext";
import SiteImage from "../components/SiteImage";
import PageHero from "../components/PageHero";
import { SectionHead } from "../components/SectionHead";
import { Reveal } from "../components/Reveal";
import { AviahWatermark } from "../components/AviahMark";
import FounderTimeline from "../components/FounderTimeline";
import FinalCta from "../components/FinalCta";
import { ButtonSecondary } from "../components/Buttons";

const About = () => {
  const { lang, t } = useLang();
  const company = useCompany();
  const a = t.about;

  return (
    <>
      <Seo title={a.seo.title} siteName={company.name} description={a.seo.description} />
      <PageHero
        eyebrow={a.eyebrow}
        lines={[a.title]}
        copy={a.intro}
        breadcrumb={[{ label: t.nav.home, to: route(lang, "home") }, { label: t.nav.about }]}
        testid="about-hero"
      />

      <section className="bg-[#F4F0E8]" data-testid="about-origin">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
          <div className="grid items-start gap-16 lg:grid-cols-2">
            <SectionHead chapter={1} eyebrow={a.origin.chapter} title={a.origin.title} tone="light" />
            <Reveal delay={0.2}>
              <p className="text-[0.95rem] leading-relaxed text-[#171717]/70 sm:text-base">{a.origin.p1}</p>
              <p className="mt-5 text-[0.95rem] leading-relaxed text-[#171717]/70 sm:text-base">{a.origin.p2}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#EEE9DF]" data-testid="about-founder">
        <AviahWatermark className="absolute -right-24 top-10 h-96 w-96 text-[#171717] opacity-[0.04]" />
        <div className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
          <div className="grid items-start gap-16 lg:grid-cols-2">
            <div>
              <SectionHead chapter={2} eyebrow={a.founderTitle} title={t.home.founder.title} tone="light" />
              <Reveal delay={0.2}>
                <p className="mt-8 text-[0.95rem] leading-relaxed text-[#171717]/70 sm:text-base">{t.home.founder.bioP1}</p>
                <p className="mt-5 text-[0.95rem] leading-relaxed text-[#171717]/70 sm:text-base">{t.home.founder.bioP2}</p>
                <p className="mt-5 text-[0.95rem] leading-relaxed text-[#171717]/70 sm:text-base">{t.home.founder.p3}</p>
              </Reveal>
              <Reveal delay={0.3}>
                <p className="mt-10 text-lg font-light text-[#171717]">{company.founder}</p>
                <p className="text-[0.7rem] uppercase tracking-[0.2em] text-[#8a6f3e]">{company.founderRole[lang]}</p>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <div className="relative">
                <div className="absolute -left-3 -top-3 h-full w-full border border-[#C6A46A]" aria-hidden="true" />
                <SiteImage
                  slot="office"
                  className="relative"
                  imgClassName="aspect-[4/3] w-full object-cover grayscale-[30%]"
                />
              </div>
            </Reveal>
          </div>
          <div className="mt-24 sm:mt-32">
            <FounderTimeline stages={t.home.founder.stages} finale={t.home.founder.finale} />
          </div>
        </div>
      </section>

      <section className="bg-[#F4F0E8]" data-testid="about-values">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
          <SectionHead chapter={3} eyebrow={a.values.chapter} title={a.values.title} tone="light" />
          <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {a.values.items.map((v, i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div className="border-t border-[#171717]/15 pt-6" data-testid={`principle-${i}`}>
                  <span className="text-2xl font-light text-[#C6A46A]">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 text-base font-normal uppercase tracking-[0.12em] text-[#171717]">{v.t}</h3>
                  <p className="mt-3 text-[0.95rem] leading-relaxed text-[#171717]/65">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F4F0E8]" data-testid="about-office">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <SectionHead chapter={4} eyebrow={a.office.chapter} title={a.office.title} copy={a.office.copy} tone="light" />
              <Reveal delay={0.25}>
                <p className="mt-6 text-sm text-[#171717]/70">
                  {company.address.street}, {company.address.suite} · {company.address.city}, {company.address.state} {company.address.zip}
                </p>
                <div className="mt-8">
                  <ButtonSecondary to={route(lang, "contact")} tone="light" testid="about-office-cta">
                    {t.nav.book}
                  </ButtonSecondary>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <SiteImage
                slot="meeting"
                className="border border-[#171717]/10"
                imgClassName="aspect-[16/10] w-full object-cover grayscale-[35%]"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <FinalCta />
    </>
  );
};

export default About;
